import { useQuery } from '@tanstack/react-query'
import { pb } from '@/lib/pocketbase'
import type { Company, Award, Bid } from '@/lib/pocketbase'

// Fetch companies list
export function useCompanies(filters?: {
  search?: string
  page?: number
  perPage?: number
}) {
  const { search, page = 1, perPage = 20 } = filters || {}

  return useQuery({
    queryKey: ['companies', filters],
    queryFn: async () => {
      const filter = search
        ? pb.filter('(name_th ~ {:search} || name_en ~ {:search} || tax_id ~ {:search})', {
            search,
          })
        : ''

      const result = await pb.collection('companies').getList<Company>(page, perPage, {
        filter,
      })
      return {
        ...result,
        items: result.items.map((item) => ({
          ...item,
          name: item.name || item.name_th || item.name_en || '',
        })),
      }
    },
  })
}

// Fetch single company
export function useCompany(companyId: string) {
  return useQuery({
    queryKey: ['company', companyId],
    queryFn: async () => {
      const company = await pb.collection('companies').getOne<Company>(companyId)
      return {
        ...company,
        name: company.name || company.name_th || company.name_en || '',
      }
    },
    enabled: !!companyId,
  })
}

// Fetch companies involved in a tender (via awards/bids)
export function useTenderCompanies(tenderId: string) {
  return useQuery({
    queryKey: ['tender-companies', tenderId],
    queryFn: async () => {
      // Get awards for this tender
      const awards = await pb.collection('awards').getList<Award>(1, 100, {
        filter: `tender = "${tenderId}"`,
        expand: 'supplier',
      })

      // Get bids for this tender
      const bids = await pb.collection('bids').getList<Bid>(1, 100, {
        filter: `tender = "${tenderId}"`,
        expand: 'supplier',
      })

      // Combine unique companies
      const companyMap = new Map<string, { company: Company; role: 'winner' | 'bidder'; award?: Award; bid?: Bid }>()

      awards.items.forEach((award) => {
        const supplierId = (award as any).supplier
        const supplierExpand = (award.expand as any)?.supplier
        if (supplierId && supplierExpand) {
          companyMap.set(supplierId, {
            company: {
              ...(supplierExpand as Company),
              name:
                (supplierExpand as any)?.name ||
                (supplierExpand as any)?.name_th ||
                (supplierExpand as any)?.name_en ||
                '',
            },
            role: 'winner',
            award,
          })
        }
      })

      bids.items.forEach((bid) => {
        const supplierId = (bid as any).supplier
        const supplierExpand = (bid.expand as any)?.supplier
        if (supplierId && supplierExpand && !companyMap.has(supplierId)) {
          companyMap.set(supplierId, {
            company: {
              ...(supplierExpand as Company),
              name:
                (supplierExpand as any)?.name ||
                (supplierExpand as any)?.name_th ||
                (supplierExpand as any)?.name_en ||
                '',
            },
            role: 'bidder',
            bid,
          })
        }
      })

      return Array.from(companyMap.values())
    },
    enabled: !!tenderId,
  })
}

// Fetch company's tender history
export function useCompanyTenders(companyId: string) {
  return useQuery({
    queryKey: ['company-tenders', companyId],
    queryFn: async () => {
      // Get all awards and bids for this company
      const [awards, bids] = await Promise.all([
        pb.collection('awards').getList<Award>(1, 50, {
          filter: `supplier = "${companyId}"`,
          expand: 'tender',
          sort: '-award_date',
        }),
        pb.collection('bids').getList<Bid>(1, 50, {
          filter: `supplier = "${companyId}"`,
          expand: 'tender',
          sort: '-submitted_at',
        }),
      ])

      return {
        awards: awards.items.map((item) => {
          const tenderExpand = (item.expand as any)?.tender
          const supplierExpand = (item.expand as any)?.supplier
          return {
            ...item,
            total_price: (item as any).total_price ?? (item as any).awarded_amount_satang,
            tender_id: (item as any).tender,
            company_id: (item as any).supplier,
            expand: {
              ...item.expand,
              tender_id: tenderExpand
                ? {
                    ...tenderExpand,
                    project_name:
                      tenderExpand.project_name ||
                      tenderExpand.title_th ||
                      '',
                  }
                : undefined,
              company_id: supplierExpand,
            },
          }
        }),
        bids: bids.items.map((item) => {
          const tenderExpand = (item.expand as any)?.tender
          const supplierExpand = (item.expand as any)?.supplier
          return {
            ...item,
            bid_price: (item as any).bid_price ?? (item as any).bid_amount_satang,
            tender_id: (item as any).tender,
            company_id: (item as any).supplier,
            expand: {
              ...item.expand,
              tender_id: tenderExpand
                ? {
                    ...tenderExpand,
                    project_name:
                      tenderExpand.project_name ||
                      tenderExpand.title_th ||
                      '',
                  }
                : undefined,
              company_id: supplierExpand,
            },
          }
        }),
        totalWins: awards.totalItems,
        totalBids: bids.totalItems,
      }
    },
    enabled: !!companyId,
  })
}
